// Price Pulse on the auto home page.
//
// Unlike the fund band, this one renders even with no data. The dataset only exists if readers
// build it, and a table nobody can see is a table nobody contributes to, so the empty state is the
// invitation rather than a hidden component.
import { useEffect, useState } from 'react';
import Link from '@/lib/siteLink';
import { ArrowRight, IndianRupee } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const inr = (n) => (typeof n === 'number' ? `₹${Math.round(n).toLocaleString('en-IN')}` : '–');

export default function PricePulseBand({ limit = 5 }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/price-pulse?limit=${limit}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [limit]);

  if (failed) return null;

  const ready = data?.ready && data?.rows?.length > 0;

  return (
    <section className="py-16 bg-white" data-testid="price-pulse-band">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[hsl(10,80%,55%)]">Price Pulse</p>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
              What people actually paid, on the road
            </h2>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              Ex-showroom is published everywhere. On-road is published nowhere, because it moves with
              your city and your dealer. This table comes from buyers.
            </p>
          </div>
          <Link to="/auto/price-pulse"
                className="text-sm text-[hsl(10,80%,55%)] hover:text-[hsl(10,80%,45%)] font-medium flex items-center gap-1 shrink-0">
            {ready ? 'All prices' : 'Add yours'} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {ready ? (
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500">
                  {['Model', 'City', 'Median paid', 'Reports'].map((h) => (
                    <th key={h} className="text-left font-semibold text-[11px] uppercase tracking-wider px-4 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={`${r.make}-${r.model}-${r.city}`} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-900">{r.make} {r.model}</td>
                    <td className="px-4 py-3 text-gray-600">{r.city}</td>
                    <td className="px-4 py-3 font-mono tabular-nums font-semibold">{inr(r.median_paid)}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono tabular-nums">n={r.n}</span>
                      {r.thin && (
                        <span className="ml-2 text-[11px] font-semibold px-2 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200">thin</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
              Middle value, not the average. Rows marked thin rest on fewer than {data.thin_below} reports.
              Unverified figures reported by readers, not price quotes.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <IndianRupee className="w-5 h-5 text-[hsl(10,80%,55%)] mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Bought a car recently?</p>
                <p className="text-sm text-gray-600 mt-0.5 max-w-xl">
                  Tell us what you paid on the road. Thirty seconds, no account. It is the number the
                  next buyer in your city cannot find anywhere else.
                </p>
              </div>
            </div>
            <Link to="/auto/price-pulse"
                  className="h-11 px-5 rounded-lg bg-[hsl(10,80%,55%)] text-white font-semibold text-sm
                             hover:bg-[hsl(10,80%,48%)] transition-colors flex items-center shrink-0">
              Add my price
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
